import * as z from 'zod';
import { emptyPrice } from '@noor/ui';
import { menuCategorySchema, menuProductSchema } from '@/entities/menu';

/** Translator shape (i18next's `t`), passed in so validation messages localize. */
type Translate = (key: string) => string;

// Modal payloads, validated at the modal registry boundary. `branches` seeds the
// branch multi-select and `categories` the category select; the `mode`
// discriminant lets one modal component serve both add and edit.

/** A merchant branch shown in the wizard's branch multi-select. */
export const productBranchSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type ProductBranch = z.infer<typeof productBranchSchema>;

/** Fields both modes share, so the two payloads cannot drift apart. */
const productPayloadBaseSchema = z.object({
  categories: z.array(menuCategorySchema),
  branches: z.array(productBranchSchema),
  /** Category pre-selected for a new product (from a category header action). */
  defaultCategoryId: z.union([z.string(), z.number()]).optional(),
});

export const addProductPayloadSchema = productPayloadBaseSchema.extend({
  mode: z.literal('add'),
});
export type AddProductPayload = z.infer<typeof addProductPayloadSchema>;

export const editProductPayloadSchema = productPayloadBaseSchema.extend({
  mode: z.literal('edit'),
  /**
   * The catalog row being edited. A PROJECTION — no gallery, one resolved
   * locale, a branch-scoped price — so it seeds the first paint while the
   * wizard re-reads the product by id for what actually gets saved.
   */
  product: menuProductSchema,
});
export type EditProductPayload = z.infer<typeof editProductPayloadSchema>;

/**
 * A product photo in the upload gallery — mirrors `@noor/ui`'s presentational
 * `ProductImage`. `file` is present only for freshly-picked uploads; `url` is a
 * remote URL (saved) or a `blob:` preview (new). One image is the cover photo
 * (`isPrimary`).
 */
export const productImageSchema = z.object({
  id: z.string(),
  url: z.string(),
  file: z.instanceof(File).optional(),
  isPrimary: z.boolean().optional(),
  /**
   * True for a photo already stored on the product. Its `url` is the backend's
   * expansion of the storage key, not the key — the read shapes carry no way
   * back to it — so a saved photo can be shown and removed but never re-sent.
   * A submit that touches no photo therefore omits `images` entirely, which the
   * PATCH body reads as "leave them alone".
   */
  saved: z.boolean().optional(),
});
export type ProductFormImage = z.infer<typeof productImageSchema>;

/**
 * A trimmed optional text input. A cleared input arrives as `''`, which would
 * otherwise be sent as a real (empty) value; this collapses it to undefined so
 * the API layer can tell "untouched" from "set to nothing".
 */
const emptyToUndefined = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  z.string().trim().optional(),
);

/**
 * Character ceilings for the free-text fields, applied to every language.
 *
 * They are a data-quality guard rather than a wire constraint — the API types
 * these as unbounded strings — so the limit is enforced where the merchant can
 * see it, next to the input, instead of surfacing as a rejected save.
 */
export const NAME_MAX_LENGTH = 100;
export const DESCRIPTION_MAX_LENGTH = 500;

/**
 * The IKPU (ИКПУ/SPIC) classifier's exact shape: seventeen digits, nothing else.
 *
 * OPTIONAL — a merchant who has not been assigned a code must still be able to
 * save — but a code that IS entered has to be well-formed, because the OFD
 * rejects a malformed classifier at fiscalization time rather than at save time,
 * where it would be actionable.
 */
export const IKPU_CODE_LENGTH = 17;

/**
 * Optional VAT %, shared by the product and each modifier option. Empty means
 * "inherit the brand's rate" (the field is then not sent); `0` is a real
 * "no VAT", so the preprocess must not collapse it to undefined.
 */
function optionalVatPercent(t: Translate) {
  return z.preprocess(
    (value) =>
      value === '' || value === null || value === undefined ? undefined : Number(value),
    z
      .number({ message: t('menu.product.form.vatPercentInvalid') })
      .min(0, t('menu.product.form.vatPercentInvalid'))
      .max(100, t('menu.product.form.vatPercentInvalid'))
      .optional(),
  );
}

/** Form values of the display-unit select; `none` = sold per item. */
export const MEASUREMENT_TYPE_VALUES = [
  'none',
  'MEASUREMENT_TYPE_GR',
  'MEASUREMENT_TYPE_KG',
  'MEASUREMENT_TYPE_ML',
  'MEASUREMENT_TYPE_L',
  'MEASUREMENT_TYPE_PCS',
  'MEASUREMENT_TYPE_PORTION',
] as const;

/**
 * The shape of a modifier group's choice control: exactly one (radio) or
 * several (checkboxes).
 *
 * As of API v1.00.227 this is EDITOR vocabulary, not a wire enum —
 * `v1SelectionType` is deleted and the independent `min_select`/`max_select`
 * pair is back. `single` writes `max_select: 1`; `multi` writes the merchant's
 * own cap (`maxSelect`) or no ceiling at all. Required-ness is its own toggle
 * (`required` → `min_select`), so the design's «Обязательно» switch and «3шт»
 * box finally have wire fields to write to.
 */
export const MODIFIER_CHOICE_TYPES = ['single', 'multi'] as const;
export const modifierChoiceTypeSchema = z.enum(MODIFIER_CHOICE_TYPES);
export type ModifierChoiceType = z.infer<typeof modifierChoiceTypeSchema>;

/**
 * One variant row inside a modifier group — a name and what it adds to the
 * price ("Большая, 45 см" / "15 000 сум").
 *
 * The name is captured in all three languages and ALL THREE ARE PERSISTED as of
 * API v1.00.180 (`v1LocalizedModifierOption.name` is a `v1LocalizedText`).
 * Only Ru is required, matching every other localized field here: uz/en may
 * genuinely not be translated yet, and the backend reads a blank as "not
 * provided" rather than clearing the stored value.
 *
 * `priceDelta` is REQUIRED but may be 0: the baseline variant of a group
 * typically costs nothing extra, and the design shows "0 сум" as a real,
 * deliberate value rather than an empty box.
 */
export function buildModifierOptionSchema(t: Translate) {
  return z.object({
    /** The server's id for an already-stored option; absent for a new one. */
    id: z.string().optional(),
    nameRu: z.string().trim().min(1, t('menu.product.form.modifiers.optionNameRequired')),
    nameUz: z.string().trim().optional(),
    nameEn: z.string().trim().optional(),
    priceDelta: emptyPrice(
      z
        .number({ message: t('menu.product.form.modifiers.optionPriceRequired') })
        .int()
        .nonnegative(t('menu.product.form.modifiers.optionPriceNonNegative')),
    ),
    /**
     * OFD fiscal classifier code for this option. Required as of v1.00.227+
     * (Swagger update: ikpu_code and unit_code are required on every option).
     */
    ikpuCode: z
      .string({ message: t('menu.product.form.modifiers.optionIkpuRequired') })
      .trim()
      .regex(
        new RegExp(`^\\d{${IKPU_CODE_LENGTH}}$`),
        t('menu.product.form.ikpuInvalid'),
      ),
    /** Soliq tasnif measurement unit code. Required on every option. */
    unitCode: z
      .string({ message: t('menu.product.form.modifiers.optionUnitRequired') })
      .trim()
      .min(1, t('menu.product.form.modifiers.optionUnitRequired')),
    /** VAT %; empty = inherit. */
    vatPercent: optionalVatPercent(t),
  });
}

/**
 * One modifier group — "Размер пиццы" with its variants.
 *
 * Like its options, the group name round-trips in all three languages
 * (`v1LocalizedModifierGroup`, API v1.00.180). Writing them takes a follow-up
 * call — the inline `modifiers` field on the product create/PATCH still carries
 * plain-string names — which `menuApi` handles; the form is unaware.
 */
export function buildModifierGroupSchema(t: Translate) {
  return z.object({
    /** The server's id for an already-stored group; absent for a new one. */
    id: z.string().optional(),
    nameRu: z.string().trim().min(1, t('menu.product.form.modifiers.groupNameRequired')),
    nameUz: z.string().trim().optional(),
    nameEn: z.string().trim().optional(),
    choiceType: modifierChoiceTypeSchema,
    /** «Обязательно» — the customer must pick at least one (`min_select` ≥ 1). */
    required: z.boolean().optional(),
    /**
     * The stored `min_select`, carried through the form UNRENDERED so that a
     * provider-imported range (Delever ships groups with min == max == 8)
     * survives a save that never touched the toggle: `required` collapses any
     * positive minimum to one boolean, and deriving `1` back from it would
     * silently rewrite an 8.
     */
    minSelect: z.number().int().nonnegative().optional(),
    /**
     * The multi-pick cap (the design's «3шт» box) — `max_select` on the wire.
     * Cleared/blank means NO ceiling; shown only for `multi`, since `single`
     * IS a cap of one. Bounded at 2+ because a multi capped at one is the
     * single case wearing the wrong control.
     */
    maxSelect: z.preprocess(
      (value) =>
        value === '' || value === null || value === undefined ? undefined : Number(value),
      z
        .number({ message: t('menu.product.form.modifiers.maxSelectInvalid') })
        .int(t('menu.product.form.modifiers.maxSelectInvalid'))
        .min(2, t('menu.product.form.modifiers.maxSelectRange'))
        .max(50, t('menu.product.form.modifiers.maxSelectRange'))
        .optional(),
    ),
    options: z
      .array(buildModifierOptionSchema(t))
      .min(1, t('menu.product.form.modifiers.optionsRequired')),
  });
}

/** One language's product name: required, and capped at {@link NAME_MAX_LENGTH}. */
function productName(t: Translate) {
  return z
    .string()
    .trim()
    .min(1, t('menu.product.form.nameRequired'))
    .max(NAME_MAX_LENGTH, t('menu.product.form.nameTooLong'));
}

/** One language's description: required, capped at {@link DESCRIPTION_MAX_LENGTH}. */
function productDescription(t: Translate) {
  return z
    .string()
    .trim()
    .min(1, t('menu.product.form.descriptionRequired'))
    .max(DESCRIPTION_MAX_LENGTH, t('menu.product.form.descriptionTooLong'));
}

/**
 * Build the add/edit-product form schema with localized messages. Price is a
 * canonical number (so'm has no minor units).
 *
 * Name and description are captured in all three platform languages, matching
 * the API's `LocalizedText`, and every one is required: the storefront resolves
 * a product to the viewer's locale, so a missing translation renders blank for
 * those customers rather than falling back to a language they can read. Each is
 * length-capped ({@link NAME_MAX_LENGTH} / {@link DESCRIPTION_MAX_LENGTH}).
 *
 * Price is REQUIRED (and > 0) when creating a product, but stays optional on
 * an edit: it is a per-branch overlay, and requiring it there blocked saving a
 * name or description fix on any product whose branch price was not set yet.
 */
export function buildProductFormSchema(t: Translate, isEditing = false) {
  return (
    z
      .object({
        nameRu: productName(t),
        nameUz: productName(t),
        nameEn: productName(t),
        descriptionRu: productDescription(t),
        descriptionUz: productDescription(t),
        descriptionEn: productDescription(t),
        compositionRu: z.string().trim().optional(),
        compositionUz: z.string().trim().optional(),
        compositionEn: z.string().trim().optional(),
        calories: z.preprocess(
          (v) => (v == null || v === '' ? undefined : v),
          z.number().int().min(0).max(100_000).optional(),
        ),
        fats: z.preprocess(
          (v) => (v == null || v === '' ? undefined : v),
          z.number().min(0).max(10_000).multipleOf(0.1).optional(),
        ),
        proteins: z.preprocess(
          (v) => (v == null || v === '' ? undefined : v),
          z.number().min(0).max(10_000).multipleOf(0.1).optional(),
        ),
        /**
         * Branches the product is sold in. Empty means "the branch being
         * edited" — the API layer resolves it, because the create endpoint
         * requires at least one and a product stocked nowhere would vanish
         * from the catalog it was just added to.
         */
        branchIds: z.array(z.string()).optional(),
        categoryId: z.preprocess(
          (value) => (value === '' || value === null ? undefined : value),
          z.union([z.string(), z.number()], {
            message: t('menu.product.form.categoryRequired'),
          }),
        ),
        alwaysReady: z.boolean().optional(),
        isAdult: z.boolean().optional(),
        /** The dish's modifiers are switched off wholesale (step 3's switch). */
        modifiersDisabled: z.boolean().optional(),
        /**
         * Preparation time in WHOLE MINUTES — merchants think in minutes; the
         * API layer converts to `cook_time_seconds`. Cleared inputs arrive as
         * '' and normalize to undefined so "empty" is distinguishable from 0
         * (which would be a real, and invalid, "no wait" answer).
         *
         * The bounds are a data-quality guard: 0 is rejected because a product
         * that needs no time is the `alwaysReady` case, and the 120-minute cap
         * mirrors the API's `cook_time_seconds` limit (max 7200s = 120min).
         */
        prepTimeMinutes: z.preprocess(
          (value) =>
            value === '' || value === null || value === undefined
              ? undefined
              : Number(value),
          z
            .number({ message: t('menu.product.form.prepTimeInvalid') })
            .int(t('menu.product.form.prepTimeInvalid'))
            .min(1, t('menu.product.form.prepTimeRange'))
            .max(120, t('menu.product.form.prepTimeRange'))
            .optional(),
        ),
        images: z.array(productImageSchema).optional(),
        /**
         * Price in so'm. REQUIRED and > 0 on create; OPTIONAL on edit, where an
         * empty box is omitted from the write ("leave the stored price alone")
         * so a name/description fix is not blocked by an unset branch price.
         *
         * `emptyPrice` collapses the cleared box's `null` to `undefined` so it
         * reads as "no price given" rather than a raw type complaint.
         */
        price: emptyPrice(
          isEditing
            ? z.number().int().positive(t('menu.product.form.pricePositive')).optional()
            : z
                .number({ message: t('menu.product.form.priceRequired') })
                .int(t('menu.product.form.pricePositive'))
                .positive(t('menu.product.form.pricePositive')),
        ),
        /**
         * OFD (fiscal receipt) classifiers.
         * Required on product creation and edit (v1.00.227+).
         */
        ikpuCode: z
          .string({ message: t('menu.product.form.ikpuRequired') })
          .trim()
          .min(1, t('menu.product.form.ikpuRequired'))
          .regex(
            new RegExp(`^\\d{${IKPU_CODE_LENGTH}}$`),
            t('menu.product.form.ikpuInvalid'),
          ),
        unitCode: z
          .string({ message: t('menu.product.form.unitCodeRequired') })
          .trim()
          .min(1, t('menu.product.form.unitCodeRequired')),
        /**
         * Net amount of one unit in the unit `unitCode` names (kg, ml, pcs…).
         * Optional; a cleared input arrives as null/'' and becomes undefined.
         */
        measureAmount: z.preprocess(
          (value) =>
            value === '' || value === null || value === undefined
              ? undefined
              : Number(value),
          z
            .number({ message: t('menu.product.form.measureAmountInvalid') })
            .positive(t('menu.product.form.measureAmountInvalid'))
            .optional(),
        ),
        /**
         * How the product's weight/volume is DISPLAYED in the client and
         * merchant apps — independent of the OFD `unitCode` above, which only
         * feeds the fiscal receipt. `none` = sold per item, no weight unit.
         */
        measurementType: z.enum(MEASUREMENT_TYPE_VALUES).default('none'),
        /** VAT %; empty = the brand's rate, 0 = no VAT. */
        vatPercent: optionalVatPercent(t),
        /**
         * Selling hours, "HH:MM". Both empty = sold all day; a single side is
         * rejected by the refine below (a half-open window is ambiguous).
         */
        availableFrom: emptyToUndefined,
        availableTo: emptyToUndefined,
        hasMarkedCode: z.boolean().optional(),
        markedCode: emptyToUndefined,
        /**
         * The product's modifier groups (wizard step 3). Always an array in the
         * form — an empty one simply means "no groups" — and it is the API layer
         * that decides between "replace with none" and "leave alone".
         */
        modifiers: z.array(buildModifierGroupSchema(t)).default([]),
      })
      // A product flagged for mandatory goods marking must carry its Data
      // Matrix code — the OFD rejects a marked-goods receipt without one, so
      // catching it here beats a fiscalization failure at checkout.
      .refine((v) => v.hasMarkedCode !== true || (v.markedCode ?? '').length > 0, {
        message: t('menu.product.form.markedCodeRequired'),
        path: ['markedCode'],
      })
      // Prep time and "always ready" are mutually exclusive answers to the same
      // question: how long the kitchen needs. A product that is NOT always ready
      // must say how long it takes, otherwise the storefront has no ETA to show.
      // (The always-ready branch needs no rule — the UI hides and clears the
      // field, so a stale value can never be submitted.)
      // Selling hours come as a pair: «с» without «до» (or the reverse) would
      // leave one side open, which the merchant cannot have meant.
      .refine((v) => (v.availableFrom === undefined) === (v.availableTo === undefined), {
        message: t('menu.product.form.sellingHoursBoth'),
        path: ['availableTo'],
      })
      .refine((v) => v.alwaysReady === true || v.prepTimeMinutes !== undefined, {
        message: t('menu.product.form.prepTimeRequired'),
        path: ['prepTimeMinutes'],
      })
  );
}

export type ProductFormValues = z.infer<ReturnType<typeof buildProductFormSchema>>;

/**
 * One modifier group's values, as the form SEEDS them.
 *
 * This is the shape `buildDefaults` produces, not the widest shape a control
 * can hold mid-edit: `emptyPrice`/`z.preprocess` type their input as `unknown`
 * (a cleared price box stores `null`), which is useful for validation but
 * useless for seeding — so the numeric fields are restated concretely. RHF's
 * own `DefaultValues<ProductFormValues>` deep-partials this at the use site,
 * which is what allows a half-filled row while the merchant is typing.
 */
export type ModifierGroupFormValues = Omit<
  z.input<ReturnType<typeof buildModifierGroupSchema>>,
  'options' | 'maxSelect'
> & {
  options: ModifierOptionFormValues[];
  maxSelect?: number;
};

/** One variant row's values, as the form seeds them. */
export type ModifierOptionFormValues = Omit<
  z.input<ReturnType<typeof buildModifierOptionSchema>>,
  'priceDelta' | 'vatPercent'
> & { priceDelta: number; vatPercent?: number };

/**
 * One modifier group AFTER validation — what a submit handler receives, with
 * every preprocessed field narrowed to its parsed type.
 */
export type ModifierGroupValues = z.output<ReturnType<typeof buildModifierGroupSchema>>;
